import { IconButton } from '@mui/material';
import { EEntityType, ELinks, entityTypeToLink } from '@sharedInterfaces/globalEnums';
import { formatDateLocalized } from '@sharedInterfaces/globalHelper';
import type { StatusOption } from '@sharedInterfaces/ICompany';
import type { IEmployeeCoverage } from '@sharedInterfaces/IOpportunity';
import { checkPermissions } from '@sharedInterfaces/IPermissions';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import type { CustomGraphQLError } from '@src/APIs/graphQl/graphQL';
import getOpportunity from '@src/APIs/graphQl/Opportunity/getOpportunity';
import getSuggestedEmployees from '@src/APIs/graphQl/Opportunity/getSuggestedEmployees';
import MvpSuggestedEmployeesCard, {
    type MvpSuggestedEmployeesCardProps,
} from '@src/App/Components/_UnderConstruction/MvpEmployeeSuggestion/MvpSuggestedEmployeesCard';
import CertificatesCard from '@src/App/Components/Cards/EntityCards/Certificates/CertificatesCard/CertificatesCard';
import CompetencesCard from '@src/App/Components/Cards/EntityCards/Competences/CompetencesCard/CompetencesCard';
import RolesCard from '@src/App/Components/Cards/EntityCards/Roles/RolesCard/RolesCard';
import SkillsCard from '@src/App/Components/Cards/EntityCards/Skills/SkillsCard/SkillsCard';
import InnerLayout from '@src/App/Components/Core/Layouts/InnerLayout/InnerLayout';
import EntityPageInformation from '@src/App/Components/Core/Layouts/LayoutElements/EntityPageInformation/EntityPageInformation';
import MvpSubNav, { type IMvpQuickAction } from '@src/App/Components/Core/MvpSubNav/MvpSubNav';
import { useDialog } from '@src/App/Components/Dialogs/DialogManager';
import ExpandableText from '@src/App/Components/General/ExpandableText/ExpandableText';
import FormatedTextInput from '@src/App/Components/General/FormularElements/formsControls/inputs/FormatedTextInput/FormatedTextInput';
import LoadingBox from '@src/App/Components/General/LoadingBox/LoadingBox';
import SuggestedEmployeesBox from '@src/App/Components/Goals/SuggestedEmployeesBox/SuggestedEmployeesBox';
import AttachmentsTable from '@src/App/Components/Tables/AttachmentsTable/AttachmentsTable';
import EntityPage from '@src/App/Pages/EntityPages/Generic/EntityPage/EntityPage';
import { combinedKnowledge } from '@src/helper/helper';
import { EIconCategory, getAppIcon } from '@src/helper/iconRegistry';
import Opportunity from '@src/Objects/Opportunity';
import { updateOpportunity } from '@store/reducer/opportunityRecuder';
import type { AppState } from '@store/store';
import { Files, UserRoundCheck } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate, useParams } from 'react-router-dom';

import MvpOpportunityDialog from './MvpOpportunityDialog';

interface MvpOpportunityPageProps {
    entity?: Opportunity;
    inDialog?: boolean;
}

/**
 * Opportunity Detail Page using EntityPage architecture
 */
function MvpOpportunityPage({ entity, inDialog }: MvpOpportunityPageProps) {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { openDialog } = useDialog();

    const { id, tab } = useParams();
    const entityId = entity ? entity.id : id ? Number.parseInt(id) : -1;
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).opportunities;
    const permissions = useSelector((state: AppState) => state.permissions);
    const readRoles = checkPermissions('Roles', 'Retrieve', permissions);
    const allCompetences = useSelector((state: AppState) => state.company.allCompetences);
    const entityState = useSelector((state: AppState) => state.opportunity.find((a) => a.id === entityId));
    const statusOptions = useSelector((state: AppState) => state.company.opportunitySettings.statusOptions);
    const statusOptionsMap = new Map<string, StatusOption>(statusOptions.map((option) => [option.id, option]));

    const [loadingError, setLoadingError] = useState<CustomGraphQLError | null>(null);
    const [suggestedEmployees, setSuggestedEmployees] = useState<IEmployeeCoverage[] | null>(null);

    // Filter out selected employees from suggestions
    const availableSuggestions =
        suggestedEmployees?.filter(
            (emp) => !entityState?.selectedEmployees?.some((selected) => selected.id === emp.id),
        ) || [];

    const { combinedSkills, combinedCertificates, combinedRoles } = combinedKnowledge(entityState, allCompetences);

    const setEntityState = (op: Opportunity) => op && dispatch(updateOpportunity(op));

    useEffect(() => {
        if (inDialog) return;
        getOpportunity(entityId).catch((ex) => {
            setLoadingError(ex);
        });
    }, [entityId, inDialog]);

    useEffect(() => {
        if (inDialog || !entityState || combinedSkills.length === 0) return;

        getSuggestedEmployees(
            combinedSkills.map((s) => ({ id: s.id, title: s.title, level: s.level })),
            combinedCertificates.map((c) => ({ id: c.id, title: c.title })),
            combinedRoles.map((r) => ({ id: r.id, title: r.title })),
        )
            .then((empData) => {
                setSuggestedEmployees(empData);
                updateSelectedEmps(empData);
            })
            .catch((ex) => {
                setLoadingError(ex);
            });
    }, [entityState, combinedSkills, combinedCertificates, combinedRoles, inDialog]);

    const updateSelectedEmps = (suggestedEmployees: IEmployeeCoverage[]) => {
        if (!entityState || !suggestedEmployees) return;
        const coppiedEntity = new Opportunity(entityState);
        coppiedEntity.selectedEmployees = [];
        entityState.selectedEmployees.forEach((selEmp) => {
            const foundObj = suggestedEmployees.find((e) => e.id === selEmp.id);
            let newEmp;
            if (foundObj) {
                // TODO: Add missingRoles and coveredRoles to EmployeeCoverageInput GraphQL schema
                newEmp = {
                    id: selEmp.id,
                    title: selEmp.title,
                    score: foundObj.score,
                    skillCoverage: foundObj.skillCoverage,
                    coveredSkills: foundObj.coveredSkills,
                    missingSkills: foundObj.missingSkills,
                    coveredCertificates: foundObj.coveredCertificates,
                    missingCertificates: foundObj.missingCertificates,
                    // Include empty arrays for roles to satisfy IEmployeeCoverage interface
                    missingRoles: foundObj.missingRoles || [],
                    coveredRoles: foundObj.coveredRoles || [],
                };
            } else {
                newEmp = {
                    id: selEmp.id,
                    title: selEmp.title,
                    score: 0,
                    skillCoverage: 0,
                    coveredSkills: [],
                    missingSkills: entityState.skills.map((s) => ({ ...s, level: 0, wantedLevel: s.level })),
                    coveredCertificates: [],
                    missingCertificates: entityState.certificates.map((s) => ({ ...s })),
                    missingRoles: [],
                    coveredRoles: [],
                };
            }
            coppiedEntity.selectedEmployees.push(newEmp);
        });
        setEntityState(coppiedEntity);
    };

    const skillCoverage = entityState
        ? Math.round(
              (entityState.selectedEmployees.reduce((prev, cur) => cur.skillCoverage + prev, 0) /
                  entityState.selectedEmployees.length) *
                  100,
          )
        : 0;
    const skillScore = entityState
        ? Math.round(
              (entityState.selectedEmployees.reduce((prev, cur) => cur.score + prev, 0) /
                  entityState.selectedEmployees.length) *
                  100,
          )
        : 0;

    const handleOpenEditDialog = async (initialTab?: 'details' | 'suggestions') => {
        return openDialog(
            <MvpOpportunityDialog open={true} onClose={() => {}} opportunity={entityState} initialTab={initialTab} />,
        );
    };

    const quickActions: IMvpQuickAction[] = [
        {
            id: 'edit',
            label: 'Anfrage bearbeiten',
            icon: getAppIcon(EIconCategory.EDIT_OPPORTUNITY),
            onClick: () => handleOpenEditDialog(),
            variant: 'primary',
        },
        {
            id: 'back',
            label: 'Zurück zur Übersicht',
            icon: getAppIcon(EIconCategory.LIST_OVERVIEW),
            onClick: () => navigate(`/${ELinks.SALES_OPPORTUNITIES}`),
            variant: 'default',
        },
    ];

    let content: JSX.Element = <LoadingBox />;

    if (entityState) {
        switch (tab?.toLowerCase()) {
            case 'attachments':
                content = <AttachmentsTable entityId={entityId} entityType={EEntityType.OPPORTUNITY} />;
                break;
            case 'dashboard':
            default:
                content = (
                    <>
                        {entityState.selectedEmployees && entityState.selectedEmployees.length > 0 && (
                            <MvpSuggestedEmployeesCard
                                {...({
                                    title: langStrings.selectedEmployee,
                                    suggestedEmployees: entityState.selectedEmployees,
                                    inDialog,
                                    customIcon: <UserRoundCheck size={20} />,
                                    rightButtons: (
                                        <IconButton onClick={() => handleOpenEditDialog('suggestions')}>
                                            {getAppIcon(EIconCategory.EDIT)}
                                        </IconButton>
                                    ),
                                } as MvpSuggestedEmployeesCardProps)}
                            />
                        )}
                        {availableSuggestions.length > 0 ? (
                            <MvpSuggestedEmployeesCard
                                suggestedEmployees={availableSuggestions}
                                inDialog={inDialog}
                                opportunity={entityState}
                                rightButtons={
                                    <IconButton onClick={() => handleOpenEditDialog('suggestions')}>
                                        {getAppIcon(EIconCategory.EDIT)}
                                    </IconButton>
                                }
                            />
                        ) : (
                            suggestedEmployees &&
                            suggestedEmployees.length > 0 && (
                                <MvpSuggestedEmployeesCard
                                    {...({
                                        title: langStrings.suggestedEmployee,
                                        suggestedEmployees: [],
                                        inDialog,
                                        emptyStateText: 'Keine weiteren Vorschläge vorhanden',
                                        rightButtons: (
                                            <IconButton onClick={() => handleOpenEditDialog('suggestions')}>
                                                {getAppIcon(EIconCategory.EDIT)}
                                            </IconButton>
                                        ),
                                    } as MvpSuggestedEmployeesCardProps)}
                                />
                            )
                        )}
                        {readRoles && (
                            <RolesCard
                                roles={entityState.roles}
                                rightButtons={
                                    <IconButton onClick={() => handleOpenEditDialog()}>
                                        {getAppIcon(EIconCategory.EDIT)}
                                    </IconButton>
                                }
                            />
                        )}
                        <CompetencesCard
                            competences={entityState.competences}
                            rightButtons={
                                <IconButton onClick={() => handleOpenEditDialog()}>
                                    {getAppIcon(EIconCategory.EDIT)}
                                </IconButton>
                            }
                        />
                        <SkillsCard
                            skills={combinedSkills}
                            rightButtons={
                                <IconButton onClick={() => handleOpenEditDialog()}>
                                    {getAppIcon(EIconCategory.EDIT)}
                                </IconButton>
                            }
                        />
                        <CertificatesCard
                            certificates={combinedCertificates}
                            rightButtons={
                                <IconButton onClick={() => handleOpenEditDialog()}>
                                    {getAppIcon(EIconCategory.EDIT)}
                                </IconButton>
                            }
                        />
                    </>
                );
                break;
        }
    }

    if (inDialog) {
        return (
            <EntityPage
                inDialog={inDialog}
                entity={entityState}
                loadingError={loadingError}
                admin={permissions.admin}
                permissions={permissions.entities.Opportunities}
                onDelete={() => {
                    if (!inDialog) navigate(`/${ELinks.SALES_OPPORTUNITIES}`);
                }}
                tabs={[
                    {
                        title: langStrings.oftenUsed.dashboard,
                        link: entityTypeToLink(entityId, EEntityType.OPPORTUNITY, 'dashboard'),
                    },
                    {
                        title: langStrings.oftenUsed.attachments,
                        link: entityTypeToLink(entityId, EEntityType.OPPORTUNITY, 'attachments'),
                    },
                ]}
                openEditDialog={handleOpenEditDialog}
                informations={
                    entityState
                        ? [
                              entityState.externalId ? (
                                  <EntityPageInformation key={'externalId'} title={langStrings.externalID}>
                                      {`${entityState.externalId}`}
                                  </EntityPageInformation>
                              ) : undefined,
                              entityState.customer ? (
                                  <EntityPageInformation key={'customer'} title={langStrings.customer}>
                                      {entityState.customer}
                                  </EntityPageInformation>
                              ) : undefined,
                              entityState.partner ? (
                                  <EntityPageInformation key={'partner'} title={langStrings.partner}>
                                      {entityState.partner}
                                  </EntityPageInformation>
                              ) : undefined,
                              <EntityPageInformation key={'utilization'} title={langStrings.utilization}>
                                  {`${Math.round(entityState.utilization * 10) / 10}%`}
                              </EntityPageInformation>,
                              <EntityPageInformation key={'remote'} title={langStrings.remote}>
                                  {`${Math.round(entityState.remote * 10) / 10}%`}
                              </EntityPageInformation>,
                              ...(entityState.dayPrice
                                  ? [
                                        <EntityPageInformation key={'dayPrice'} title={langStrings.dayPrice}>
                                            {`${entityState.dayPrice?.toLocaleString('de-DE')}€`}
                                        </EntityPageInformation>,
                                    ]
                                  : []),
                              entityState.projectStart ? (
                                  <EntityPageInformation key={'projectStart'} title={langStrings.startDate}>
                                      {formatDateLocalized(entityState.projectStart, lang)}
                                  </EntityPageInformation>
                              ) : undefined,
                              entityState.projectEnd ? (
                                  <EntityPageInformation key={'endDate'} title={langStrings.endDate}>
                                      {formatDateLocalized(entityState.projectEnd, lang)}
                                  </EntityPageInformation>
                              ) : undefined,
                              entityState.publishDate ? (
                                  <EntityPageInformation key={'publishDate'} title={langStrings.publishDate}>
                                      {formatDateLocalized(entityState.publishDate, lang)}
                                  </EntityPageInformation>
                              ) : undefined,
                              entityState.deadline ? (
                                  <EntityPageInformation key={'deadline'} title={langStrings.deadline}>
                                      {formatDateLocalized(entityState.deadline, lang)}
                                  </EntityPageInformation>
                              ) : undefined,
                              entityState.headCount ? (
                                  <EntityPageInformation key={'headCount'} title={langStrings.employees}>
                                      {`${entityState.headCount}`}
                                  </EntityPageInformation>
                              ) : undefined,
                              entityState.primaryRole ? (
                                  <EntityPageInformation key={'primaryRole'} title={langStrings.primaryRole}>
                                      <Link to={entityTypeToLink(entityState.primaryRole.id, EEntityType.ROLE)}>
                                          {entityState.primaryRole.title}
                                      </Link>
                                  </EntityPageInformation>
                              ) : undefined,
                              <EntityPageInformation key={'status'} title={langStrings.status}>
                                  {statusOptionsMap.get(entityState.status)?.title || ''}
                              </EntityPageInformation>,
                              <EntityPageInformation key={'skillScore'} title={langStrings.skillScore}>
                                  {`${skillCoverage ? Math.round(skillScore * 10) / 10 : 0}%`}
                              </EntityPageInformation>,
                              entityState.sourceName !== '' || entityState.sourceURL !== '' ? (
                                  <EntityPageInformation key={'source'} title={langStrings.sourceName}>
                                      {entityState.sourceName !== '' ? (
                                          <Link to={entityState.sourceURL} target="_blank" rel="noopener noreferrer">
                                              {entityState.sourceName}
                                          </Link>
                                      ) : (
                                          entityState.sourceName
                                      )}
                                  </EntityPageInformation>
                              ) : undefined,
                              entityState.requestText ? (
                                  <EntityPageInformation
                                      key={'description'}
                                      title={langStrings.opportunity}
                                      size={'full'}
                                  >
                                      <ExpandableText>
                                          <FormatedTextInput value={entityState.requestText} readonly={true} />
                                      </ExpandableText>
                                  </EntityPageInformation>
                              ) : undefined,
                          ]
                        : []
                }
            >
                {content}
            </EntityPage>
        );
    }

    return (
        <>
            <MvpSubNav currentPageTitle={entityState?.title || langStrings.opportunity} quickActions={quickActions} />
            <InnerLayout>
                <EntityPage
                    inDialog={inDialog}
                    entity={entityState}
                    loadingError={loadingError}
                    admin={permissions.admin}
                    permissions={permissions.entities.Opportunities}
                    onDelete={() => {
                        if (!inDialog) navigate(`/${ELinks.SALES_OPPORTUNITIES}`);
                    }}
                    tabs={[
                        {
                            title: langStrings.oftenUsed.dashboard,
                            link: entityTypeToLink(entityId, EEntityType.OPPORTUNITY, 'dashboard'),
                        },
                        {
                            title: langStrings.oftenUsed.attachments,
                            link: entityTypeToLink(entityId, EEntityType.OPPORTUNITY, 'attachments'),
                        },
                    ]}
                    openEditDialog={handleOpenEditDialog}
                    // TODO: DEVELOPER - Backend Integration: Add shortDescription to Opportunity GraphQL type
                    subTitle={
                        (entityState as any)?.shortDescription ||
                        (entityState?.customer
                            ? `${entityState.customer} • ${statusOptionsMap.get(entityState.status)?.title || ''}`
                            : entityState
                              ? statusOptionsMap.get(entityState.status)?.title
                              : undefined)
                    }
                    informations={
                        entityState
                            ? [
                                  entityState.externalId ? (
                                      <EntityPageInformation
                                          key={'externalId'}
                                          title={langStrings.externalID}
                                          icon={getAppIcon(EIconCategory.EXTERNAL_ID)}
                                      >
                                          {`${entityState.externalId}`}
                                      </EntityPageInformation>
                                  ) : undefined,
                                  entityState.customer ? (
                                      <EntityPageInformation
                                          key={'customer'}
                                          title={langStrings.customer}
                                          icon={getAppIcon(EIconCategory.COMPANY)}
                                      >
                                          {entityState.customer}
                                      </EntityPageInformation>
                                  ) : undefined,
                                  entityState.partner ? (
                                      <EntityPageInformation
                                          key={'partner'}
                                          title={langStrings.partner}
                                          icon={getAppIcon(EIconCategory.CUSTOMERS)}
                                      >
                                          {entityState.partner}
                                      </EntityPageInformation>
                                  ) : undefined,
                                  <EntityPageInformation
                                      key={'utilization'}
                                      title={langStrings.utilization}
                                      icon={getAppIcon(EIconCategory.UTILIZATION)}
                                  >
                                      {`${Math.round(entityState.utilization * 10) / 10}%`}
                                  </EntityPageInformation>,
                                  <EntityPageInformation
                                      key={'remote'}
                                      title={langStrings.remote}
                                      icon={getAppIcon(EIconCategory.REMOTE)}
                                  >
                                      {`${Math.round(entityState.remote * 10) / 10}%`}
                                  </EntityPageInformation>,
                                  ...(entityState.dayPrice
                                      ? [
                                            <EntityPageInformation key={'dayPrice'} title={langStrings.dayPrice}>
                                                {`${entityState.dayPrice?.toLocaleString('de-DE')}€`}
                                            </EntityPageInformation>,
                                        ]
                                      : []),
                                  entityState.projectStart ? (
                                      <EntityPageInformation
                                          key={'projectStart'}
                                          title={langStrings.startDate}
                                          icon={getAppIcon(EIconCategory.START_DATE)}
                                      >
                                          {formatDateLocalized(entityState.projectStart, lang)}
                                      </EntityPageInformation>
                                  ) : undefined,
                                  entityState.projectEnd ? (
                                      <EntityPageInformation
                                          key={'endDate'}
                                          title={langStrings.endDate}
                                          icon={getAppIcon(EIconCategory.PROJECT_END)}
                                      >
                                          {formatDateLocalized(entityState.projectEnd, lang)}
                                      </EntityPageInformation>
                                  ) : undefined,
                                  entityState.publishDate ? (
                                      <EntityPageInformation
                                          key={'publishDate'}
                                          title={langStrings.publishDate}
                                          icon={getAppIcon(EIconCategory.PUBLISHED_DATE)}
                                      >
                                          {formatDateLocalized(entityState.publishDate, lang)}
                                      </EntityPageInformation>
                                  ) : undefined,
                                  entityState.deadline ? (
                                      <EntityPageInformation
                                          key={'deadline'}
                                          title={langStrings.deadline}
                                          icon={getAppIcon(EIconCategory.DEADLINE)}
                                      >
                                          {formatDateLocalized(entityState.deadline, lang)}
                                      </EntityPageInformation>
                                  ) : undefined,
                                  entityState.headCount ? (
                                      <EntityPageInformation
                                          key={'headCount'}
                                          title={langStrings.employees}
                                          icon={getAppIcon(EIconCategory.USERS)}
                                      >
                                          {`${entityState.headCount}`}
                                      </EntityPageInformation>
                                  ) : undefined,
                                  entityState.primaryRole ? (
                                      <EntityPageInformation
                                          key={'primaryRole'}
                                          title={langStrings.primaryRole}
                                          icon={getAppIcon(EIconCategory.ROLES)}
                                      >
                                          <Link to={entityTypeToLink(entityState.primaryRole.id, EEntityType.ROLE)}>
                                              {entityState.primaryRole.title}
                                          </Link>
                                      </EntityPageInformation>
                                  ) : undefined,
                                  <EntityPageInformation
                                      key={'status'}
                                      title={langStrings.status}
                                      icon={getAppIcon(EIconCategory.GENERAL)}
                                  >
                                      {statusOptionsMap.get(entityState.status)?.title || ''}
                                  </EntityPageInformation>,
                                  <EntityPageInformation
                                      key={'skillScore'}
                                      title={langStrings.skillScore}
                                      icon={getAppIcon(EIconCategory.SKILLS)}
                                  >
                                      {`${skillCoverage ? Math.round(skillScore * 10) / 10 : 0}%`}
                                  </EntityPageInformation>,
                                  entityState.sourceName !== '' || entityState.sourceURL !== '' ? (
                                      <EntityPageInformation
                                          key={'source'}
                                          title={langStrings.sourceName}
                                          icon={getAppIcon(EIconCategory.EXTERNAL_ID)}
                                      >
                                          {entityState.sourceName !== '' ? (
                                              <Link
                                                  to={entityState.sourceURL}
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                              >
                                                  {entityState.sourceName}
                                              </Link>
                                          ) : (
                                              entityState.sourceName
                                          )}
                                      </EntityPageInformation>
                                  ) : undefined,
                                  entityState.requestText ? (
                                      <EntityPageInformation
                                          key={'description'}
                                          title={langStrings.opportunity}
                                          size={'full'}
                                      >
                                          <ExpandableText>
                                              <FormatedTextInput value={entityState.requestText} readonly={true} />
                                          </ExpandableText>
                                      </EntityPageInformation>
                                  ) : undefined,
                              ]
                            : []
                    }
                >
                    {content}
                </EntityPage>
            </InnerLayout>
        </>
    );
}

export default MvpOpportunityPage;
